import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, theme, Button, notification, Drawer, Grid } from "antd";
import { MenuOutlined } from "@ant-design/icons";

import { useDispatch } from "react-redux";
import { setToken } from "store/slice/index";

import { router } from "router";

import { apiGetMe } from "api/api";

const Root = () => {
  const [role, setRole] = useState(() => {
    const stored = sessionStorage.role;
    return stored && stored !== "undefined" ? Number(stored) : undefined;
  });
  const [name, setName] = useState(sessionStorage.name || "");
  const [activeKey, setActiveKey] = useState(["events"]);

  // # 取得當前使用者權限
  const fetchMe = async () => {
    const res = await apiGetMe({});
    const { content } = res.data;
    // 相容新舊 API：舊版回傳 role (數字)，新版回傳 roles (陣列)
    let roleLevel = content.role;
    if (roleLevel === undefined && Array.isArray(content.roles)) {
      roleLevel =
        content.roles.includes("root") || content.roles.includes("admin")
          ? 32
          : 1;
    }
    setRole(roleLevel);
    setName(content.name);
    sessionStorage.role = roleLevel;
    sessionStorage.name = content.name;
    setTimeout(() => {
      if (roleLevel >= 16) navigate("/events");
      else navigate("/scan");
    }, 500);
  };

  useEffect(() => {
    if (!sessionStorage.role || sessionStorage.role === "undefined") fetchMe();
  }, []);

  // # notification
  const [api, contextHolder] = notification.useNotification();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const id = sessionStorage.target_id;

  const { routes } = router;

  // # 響應式斷點
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // # 手機版 Drawer 狀態
  const [drawerOpen, setDrawerOpen] = useState(false);

  // # 判斷是否是全域/主選單頁面
  const isInnerSideMenu =
    location.pathname.includes("/events") ||
    location.pathname.includes("/scan") ||
    location.pathname.includes("/members");

  // 將登入頁面過濾掉 (不出現在 side menu)
  const routesFilterLogIn = routes.filter((item) => item.key !== "login")[0]
    .children;

  // # 第一層菜單, 活動列表 | 會員中心 | 票券驗證 && 加上角色等級驗證
  const routesFirst = routesFilterLogIn.filter(
    (item) =>
      (item.key === "events" ||
        item.key === "scan" ||
        item.key === "members") &&
      role >= item.level,
  );

  const routesInsideEvents = routesFilterLogIn.filter(
    (item) =>
      item.key !== "events" &&
      item.key !== "scan" &&
      item.key !== "members",
  );

  const items = (isInnerSideMenu ? routesFirst : routesInsideEvents).filter(
    (item) => item.label,
  );

  const { Header, Content, Footer, Sider } = Layout;
  const [collapsed, setCollapsed] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleRoute = ({ item, key, keyPath, domEvent }) => {
    if (key === "backToEvents") setActiveKey(["events"]);
    else setActiveKey([key]);
    let new_path = item.props.path;
    if (new_path.includes("/:id")) {
      new_path = new_path.replace(":id", id);
      navigate(`${new_path}`);
    } else navigate(`${item.props.path}`);
    // 手機版點選後關閉 Drawer
    setDrawerOpen(false);
  };

  const logOut = async () => {
    try {
      dispatch(setToken(""));
      sessionStorage.removeItem("t");
      sessionStorage.removeItem("role");
      sessionStorage.removeItem("name");
      api.success({
        message: `登出成功!`,
        description: "",
      });
      setTimeout(() => {
        navigate("/login");
      }, 500);
    } catch (err) {}
  };

  // # 側邊欄內容（桌機 Sider 和手機 Drawer 共用）
  const SidebarContent = (
    <>
      <div className="h-12 bg-[url('assets/Logo.png')] bg-no-repeat bg-center" />
      <Menu
        theme="dark"
        selectedKeys={activeKey}
        mode="inline"
        items={items}
        onClick={handleRoute}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {contextHolder}

      {/* 桌機版 Sider */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          className="fixed h-full"
        >
          {SidebarContent}
        </Sider>
      )}

      {/* 手機版 Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={220}
          styles={{
            body: { padding: 0, background: "#001529" },
            header: { background: "#001529", borderBottom: "none" },
          }}
          closeIcon={<span style={{ color: "#fff" }}>✕</span>}
        >
          {SidebarContent}
        </Drawer>
      )}

      <Layout
        className={`bg-Gray-1 ${
          isMobile ? "ml-0" : collapsed ? "w-sp2 ml-80" : "w-sp1 ml-200"
        }`}
      >
        <Header
          style={{
            padding: 0,
            background: colorBgContainer,
          }}
          className="shadow-lg"
        >
          <div className="flex h-full items-center justify-between px-4">
            <div className="flex items-center gap-3">
              {/* 手機版漢堡按鈕 */}
              {isMobile && (
                <Button
                  type="text"
                  icon={<MenuOutlined style={{ fontSize: 20 }} />}
                  onClick={() => setDrawerOpen(true)}
                />
              )}
              {/* 手機版顯示 Logo 文字 */}
              {isMobile && (
                <span className="font-bold text-base">後台管理</span>
              )}
            </div>
            <div className="flex items-center">
              <span className="mr-2 text-sm">Hi! {name}</span>
              <Button
                type="link"
                className="self-center p-0 pr-2"
                onClick={logOut}
              >
                登出
              </Button>
            </div>
          </div>
        </Header>
        <Content
          style={{
            margin: isMobile ? "0px" : "16px 24px",
          }}
          className={isMobile ? "px-3" : "px-16"}
        >
          <Outlet />
        </Content>
        <Footer
          style={{
            textAlign: "center",
          }}
          className="bg-Gray-1 text-xs"
        >
          SkyLine Film ©2024 Created by WeiHui
        </Footer>
      </Layout>
    </Layout>
  );
};

export default Root;
